import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Button from './Button';
import Input from './Input';
import Loader from './Loader';
import TokenSplitChart, {
  COMMUNITY_COLOR,
  COMMERCIAL_COLOR
} from './TokenSplitChart';
import { COMMUNITY_ADMIN_PATH } from '../../AdminDashboards/adminTools';

// Account management records whose surveys count as community surveys
const DEFAULT_COMMUNITY_RECORD_IDS = [
  '64706ddfc46fd22609075958',
  '64d4db471b082e258b507e50',
  '64c22dc7dfaa445d9faabeab',
  '64c22db7dfaa445d9faabea3',
  '64c22db0dfaa445d9faabe9f',
  '64c22da8dfaa445d9faabe9b',
  '6470805efa568c71856a7a6c'
];

const DEFAULT_DAYS = 30;

const GET_USER_COST_OVERVIEW = gql`
  query getUserCostOverview(
    $user: String
    $from: String
    $to: String
    $communityRecordIds: [ID!]
  ) {
    getUserCostOverview(
      user: $user
      from: $from
      to: $to
      communityRecordIds: $communityRecordIds
    ) {
      from
      to
      communitySurveyCount
      user {
        id
        email
        nickname
      }
      communityRecords {
        id
        name
        found
      }
      earned {
        communityTokens
        commercialTokens
        otherTokens
        totalTokens
        communityPerUser
        commercialPerUser
        otherPerUser
        totalPerUser
        communitySharePercent
        commercialSharePercent
        users
      }
      purchases {
        count
        users
        totalTokens
        averageTokensPerPurchase
        averageTokensPerUser
        communitySharePercent
        commercialSharePercent
        averageCommunityTokensPerPurchase
        averageCommercialTokensPerPurchase
      }
      churn {
        retentionDays
        retentionMeasurable
        diet {
          diet
          users
          retainedUsers
          retainedPercent
          tokensPerUser
        }
        drySpell {
          days
          returned
          notReturned
          neverReturnedPercent
        }
      }
      timeline {
        date
        communityTokens
        commercialTokens
      }
    }
  }
`;

const toInputDate = date => date.toISOString().slice(0, 10);

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_DAYS - 1));
  return { from: toInputDate(from), to: toInputDate(to) };
};

const formatTokens = value =>
  (value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatPercent = value => `${(value || 0).toFixed(1)}%`;

const formatCount = value => (value || 0).toLocaleString();

const DIET_LABELS = {
  community: 'Mostly community surveys',
  mixed: 'Both sides',
  commercial: 'Mostly commercial surveys'
};

const DIET_ACCENTS = {
  community: COMMUNITY_COLOR,
  commercial: COMMERCIAL_COLOR
};

function StatTile({ label, value, detail, accent }) {
  return (
    <div style={styles.tile}>
      <div style={styles.tileLabel}>
        {accent ? (
          <span style={{ ...styles.tileSwatch, background: accent }} />
        ) : null}
        {label}
      </div>
      <div style={styles.tileValue}>{value}</div>
      {detail ? <div style={styles.tileDetail}>{detail}</div> : null}
    </div>
  );
}

function UserCostOverview() {
  const range = defaultRange();

  const [recordIds, setRecordIds] = useState(DEFAULT_COMMUNITY_RECORD_IDS);
  const [user, setUser] = useState('');
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [showTable, setShowTable] = useState(false);
  const [applied, setApplied] = useState({
    communityRecordIds: DEFAULT_COMMUNITY_RECORD_IDS,
    user: null,
    from: range.from,
    to: range.to
  });

  const { data, loading, error } = useQuery(GET_USER_COST_OVERVIEW, {
    fetchPolicy: 'network-only',
    variables: applied
  });

  const overview = data && data.getUserCostOverview;
  const earned = overview && overview.earned;
  const purchases = overview && overview.purchases;
  const churn = overview && overview.churn;

  const onApply = () =>
    setApplied({
      communityRecordIds: recordIds.map(id => id.trim()).filter(id => id),
      user: user.trim() ? user.trim() : null,
      from,
      to
    });

  const onRecordChange = (index, value) =>
    setRecordIds(recordIds.map((id, i) => (i === index ? value : id)));

  const recordName = id => {
    const record =
      overview &&
      overview.communityRecords &&
      overview.communityRecords.find(r => r.id === id.trim());
    if (!record) return null;
    return record.found ? record.name || 'No name' : 'Record not found';
  };

  return (
    <div style={styles.container}>
      <Link to={COMMUNITY_ADMIN_PATH} style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>User cost</h1>
      <p style={styles.intro}>
        Tokens earned from community and commercial surveys, and what share of
        the purchases each of them paid for.
      </p>

      <h2 style={styles.sectionTitle}>Community records</h2>
      <p style={styles.sectionIntro}>
        Surveys of these account management records count as community surveys.
        {overview
          ? ` ${overview.communitySurveyCount} survey${
              overview.communitySurveyCount === 1 ? '' : 's'
            } matched.`
          : ''}
      </p>
      <div style={styles.recordList}>
        {recordIds.map((id, index) => (
          // eslint-disable-next-line react/no-array-index-key -- the position is what identifies a row
          <div style={styles.recordRow} key={index}>
            <Input
              value={id}
              onChange={e => onRecordChange(index, e.target.value)}
              placeholder="Account management record id"
              style={styles.recordInput}
            />
            <span style={styles.recordName}>{recordName(id)}</span>
            <span
              style={styles.recordRemove}
              role="presentation"
              onClick={() =>
                setRecordIds(recordIds.filter((_id, i) => i !== index))
              }
            >
              &times;
            </span>
          </div>
        ))}
      </div>
      <Button onClick={() => setRecordIds([...recordIds, ''])}>
        + Add record id
      </Button>

      <h2 style={styles.sectionTitle}>Period and user</h2>
      <div style={styles.filterRow}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>User id or email (optional)</span>
          <Input
            value={user}
            onChange={e => setUser(e.target.value)}
            placeholder="All users"
          />
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>From</span>
          <Input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>To</span>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <Button primary onClick={onApply} disabled={loading}>
          Apply
        </Button>
      </div>

      {error ? <p style={styles.error}>{error.message}</p> : null}
      {loading ? <Loader /> : null}

      {overview && !loading ? (
        <>
          <h2 style={styles.sectionTitle}>
            Tokens earned{' '}
            <span style={styles.count}>
              {overview.user
                ? overview.user.email || overview.user.id
                : `${earned.users} user${
                    earned.users === 1 ? '' : 's'
                  } with activity`}
            </span>
          </h2>
          <div style={styles.tiles}>
            <StatTile
              label="Community surveys"
              value={formatTokens(earned.communityTokens)}
              detail={`${formatPercent(
                earned.communitySharePercent
              )} of survey tokens`}
              accent={COMMUNITY_COLOR}
            />
            <StatTile
              label="Commercial surveys"
              value={formatTokens(earned.commercialTokens)}
              detail={`${formatPercent(
                earned.commercialSharePercent
              )} of survey tokens`}
              accent={COMMERCIAL_COLOR}
            />
            <StatTile
              label="Not from a survey"
              value={formatTokens(earned.otherTokens)}
              detail="Admin changes, automations"
            />
            <StatTile
              label="Average a user"
              value={formatTokens(earned.totalPerUser)}
              detail={`${formatTokens(
                earned.communityPerUser
              )} community · ${formatTokens(
                earned.commercialPerUser
              )} commercial`}
            />
          </div>

          <div style={styles.chartHeader}>
            <div style={styles.legend}>
              <span style={styles.legendItem}>
                <span
                  style={{
                    ...styles.legendSwatch,
                    background: COMMUNITY_COLOR
                  }}
                />
                Community
              </span>
              <span style={styles.legendItem}>
                <span
                  style={{
                    ...styles.legendSwatch,
                    background: COMMERCIAL_COLOR
                  }}
                />
                Commercial
              </span>
            </div>
            <Button onClick={() => setShowTable(!showTable)}>
              {showTable ? 'Show chart' : 'Show table'}
            </Button>
          </div>

          {showTable ? (
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.thNumber}>Community</th>
                    <th style={styles.thNumber}>Commercial</th>
                    <th style={styles.thNumber}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.timeline.map(point => (
                    <tr key={point.date}>
                      <td style={styles.td}>{point.date}</td>
                      <td style={styles.tdNumber}>
                        {formatTokens(point.communityTokens)}
                      </td>
                      <td style={styles.tdNumber}>
                        {formatTokens(point.commercialTokens)}
                      </td>
                      <td style={styles.tdNumber}>
                        {formatTokens(
                          point.communityTokens + point.commercialTokens
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <TokenSplitChart data={overview.timeline} />
          )}

          <h2 style={styles.sectionTitle}>
            Purchases{' '}
            <span style={styles.count}>
              {purchases.count} purchase{purchases.count === 1 ? '' : 's'} by{' '}
              {purchases.users} user{purchases.users === 1 ? '' : 's'}
            </span>
          </h2>
          <div style={styles.tiles}>
            <StatTile
              label="Tokens spent"
              value={formatTokens(purchases.totalTokens)}
              detail={`${formatTokens(
                purchases.averageTokensPerUser
              )} a user on average`}
            />
            <StatTile
              label="Average a purchase"
              value={formatTokens(purchases.averageTokensPerPurchase)}
            />
            <StatTile
              label="Paid with community tokens"
              value={formatTokens(purchases.averageCommunityTokensPerPurchase)}
              detail={`${formatPercent(
                purchases.communitySharePercent
              )} of an average purchase`}
              accent={COMMUNITY_COLOR}
            />
            <StatTile
              label="Paid with commercial tokens"
              value={formatTokens(purchases.averageCommercialTokensPerPurchase)}
              detail={`${formatPercent(
                purchases.commercialSharePercent
              )} of an average purchase`}
              accent={COMMERCIAL_COLOR}
            />
          </div>
          <p style={styles.note}>
            Tokens are spent from one balance, so a purchase can not be traced
            back to the surveys that paid for it. The split above applies the
            share of the tokens earned in this period to what was spent in it.
          </p>

          <h2 style={styles.sectionTitle}>
            Who stayed{' '}
            <span style={styles.count}>
              {churn.retentionMeasurable
                ? `still active ${churn.retentionDays} days after the period`
                : `the period has to be over for ${
                    churn.retentionDays
                  } days to read this`}
            </span>
          </h2>
          <p style={styles.sectionIntro}>
            Users of this period by the side that paid most of their tokens.
            When both sides keep the same share of their users, the community
            surveys are not what holds them.
          </p>
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Earned from</th>
                  <th style={styles.thNumber}>Users</th>
                  <th style={styles.thNumber}>Still active</th>
                  <th style={styles.thNumber}>Tokens a user</th>
                </tr>
              </thead>
              <tbody>
                {churn.diet.map(entry => (
                  <tr key={entry.diet}>
                    <td style={styles.td}>
                      {DIET_ACCENTS[entry.diet] ? (
                        <span
                          style={{
                            ...styles.tileSwatch,
                            background: DIET_ACCENTS[entry.diet]
                          }}
                        />
                      ) : null}
                      {DIET_LABELS[entry.diet] || entry.diet}
                    </td>
                    <td style={styles.tdNumber}>{formatCount(entry.users)}</td>
                    <td style={styles.tdNumber}>
                      {entry.retainedPercent === null
                        ? '-'
                        : `${formatCount(
                            entry.retainedUsers
                          )} · ${formatPercent(entry.retainedPercent)}`}
                    </td>
                    <td style={styles.tdNumber}>
                      {formatTokens(entry.tokensPerUser)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={styles.sectionTitle}>
            How long they wait{' '}
            <span style={styles.count}>idle time between two earnings</span>
          </h2>
          <p style={styles.sectionIntro}>
            Every time a user went this long without earning: how often they
            came back, and how often they have not earned since. The length
            where the last column jumps is how long a dry month may last.
          </p>
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Idle for</th>
                  <th style={styles.thNumber}>Came back</th>
                  <th style={styles.thNumber}>Still away</th>
                  <th style={styles.thNumber}>Never earned again</th>
                </tr>
              </thead>
              <tbody>
                {churn.drySpell.map(point => (
                  <tr key={point.days}>
                    <td style={styles.td}>{point.days} days or more</td>
                    <td style={styles.tdNumber}>
                      {formatCount(point.returned)}
                    </td>
                    <td style={styles.tdNumber}>
                      {formatCount(point.notReturned)}
                    </td>
                    <td style={styles.tdNumber}>
                      {formatPercent(point.neverReturnedPercent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#007bff',
    textDecoration: 'none'
  },
  intro: {
    color: '#666'
  },
  sectionIntro: {
    color: '#666',
    fontSize: '13px',
    marginTop: 0
  },
  error: {
    color: '#c0392b'
  },
  note: {
    color: '#8b949e',
    fontSize: '12px',
    maxWidth: '640px'
  },
  sectionTitle: {
    marginTop: '35px',
    marginBottom: '5px',
    fontSize: '18px'
  },
  count: {
    fontSize: '13px',
    fontWeight: 'normal',
    color: '#666'
  },
  recordList: {
    marginBottom: '10px'
  },
  recordRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '6px'
  },
  recordInput: {
    width: '280px',
    fontFamily: 'monospace'
  },
  recordName: {
    fontSize: '12px',
    color: '#57606a',
    minWidth: '160px'
  },
  recordRemove: {
    cursor: 'pointer',
    color: '#8b949e',
    fontSize: '16px',
    lineHeight: '16px'
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '10px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  fieldLabel: {
    fontSize: '12px',
    color: '#57606a'
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '15px',
    margin: '15px 0 25px'
  },
  tile: {
    border: '1px solid #eaecef',
    borderRadius: '8px',
    padding: '15px'
  },
  tileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#57606a'
  },
  tileSwatch: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block'
  },
  tileValue: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#24292f',
    marginTop: '6px'
  },
  tileDetail: {
    fontSize: '12px',
    color: '#8b949e',
    marginTop: '4px'
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '10px'
  },
  legend: {
    display: 'flex',
    gap: '18px',
    fontSize: '13px',
    color: '#57606a'
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  legendSwatch: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block'
  },
  tableScroll: {
    overflowX: 'auto',
    maxHeight: '320px',
    overflowY: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    borderBottom: '2px solid #ddd',
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: '12px',
    backgroundColor: '#f2f2f2'
  },
  thNumber: {
    borderBottom: '2px solid #ddd',
    padding: '8px 10px',
    textAlign: 'right',
    fontSize: '12px',
    backgroundColor: '#f2f2f2'
  },
  td: {
    borderBottom: '1px solid #eaecef',
    padding: '6px 10px',
    fontSize: '13px'
  },
  tdNumber: {
    borderBottom: '1px solid #eaecef',
    padding: '6px 10px',
    fontSize: '13px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  }
};

export default UserCostOverview;
